import axios from '../../../axios';

type ApiRes<T> = { EC: number; EM: string; DT: T };

export type Provider = 'mistral_paid' | 'mistral_free' | 'custom_ai' | 'gemini' | 'ollama_local' | 'paddleocr_local';
export type FeatureKey = 'detect_cccd' | 'detect_gplx' | 'generate_portrait';
export type KeyStatus = 'active' | 'rate_limited' | 'expired' | 'disabled';

export type Feature = {
  key: FeatureKey;
  label: string;
  supports: Provider[];
};

export type ProviderOption = {
  defaultModel?: string;
  models?: { value: string; label: string }[];
};

export type Registry = {
  providers: Provider[];
  /** Providers that don't need a key row — fixed endpoint per machine.
   *  UI hides the "+ Add key" form for these and renders a read-only
   *  health card instead. */
  keylessProviders?: Provider[];
  features: Feature[];
  defaults: Record<FeatureKey, { engines: Provider[]; parallel: boolean }>;
  providerOptions?: Partial<Record<Provider, ProviderOption & { endpoint?: string }>>;
};

export type KeylessProviderHealth = {
  ok: boolean;
  warm?: boolean;
  model?: string;
  loadedAt?: number | null;
  error?: string;
};

export type AiKey = {
  id: number;
  adminId: number;
  provider: Provider;
  label: string;
  secretMasked: string;
  hasSecret: boolean;
  endpointUrl: string | null;
  model: string | null;
  status: KeyStatus;
  failCount: number;
  rateLimitedUntil: string | null;
  lastUsedAt: string | null;
  lastErrorMessage: string | null;
  createdBy: number | null;
  createdAt: string;
  updatedAt: string;
};

export type KeyPayload = {
  provider: Provider;
  label: string;
  secret?: string;
  endpointUrl?: string;
  model?: string;
};

export type AiConfig = Record<FeatureKey, { engines: Provider[]; parallel: boolean }>;

export type AiConfigResponse = {
  adminId: number;
  aiConfig: AiConfig;
  hasCustom: boolean;
};

export type TestResult =
  | { ok: true; latencyMs: number }
  | { ok: false; error: string; status?: number };

const unwrap = <T,>(p: Promise<{ data: ApiRes<T> }>): Promise<ApiRes<T>> =>
  p.then((r) => r.data);

export const fetchRegistry = () =>
  unwrap(axios.get<ApiRes<Registry>>('/api/admin/ai/registry'));

export const fetchAllKeys = () =>
  unwrap(axios.get<ApiRes<AiKey[]>>('/api/admin/ai/keys'));

export const fetchKeysForAdmin = (adminId: number) =>
  unwrap(axios.get<ApiRes<AiKey[]>>(`/api/admin/ai/keys/${adminId}`));

export const createKey = (adminId: number, body: KeyPayload) =>
  unwrap(axios.post<ApiRes<AiKey>>(`/api/admin/ai/keys/${adminId}`, body));

export const updateKey = (keyId: number, body: Partial<KeyPayload & { status: KeyStatus }>) =>
  unwrap(axios.put<ApiRes<AiKey>>(`/api/admin/ai/key/${keyId}`, body));

export const deleteKey = (keyId: number) =>
  unwrap(axios.delete<ApiRes<{ id: number }>>(`/api/admin/ai/key/${keyId}`));

export const resetKey = (keyId: number) =>
  unwrap(axios.post<ApiRes<AiKey>>(`/api/admin/ai/key/${keyId}/reset`, {}));

export const testKey = (keyId: number) =>
  unwrap(axios.post<ApiRes<TestResult>>(`/api/admin/ai/key/${keyId}/test`, {}));

export const fetchConfig = (adminId: number) =>
  unwrap(axios.get<ApiRes<AiConfigResponse>>(`/api/admin/ai/config/${adminId}`));

export const saveConfig = (adminId: number, aiConfig: AiConfig) =>
  unwrap(axios.put<ApiRes<{ id: number }>>(`/api/admin/ai/config/${adminId}`, { aiConfig }));

export const pingKeylessProvider = (provider: Provider) =>
  unwrap(axios.get<ApiRes<KeylessProviderHealth>>(`/api/admin/ai/keyless/${provider}/health`));
